/**
 * Symphony GROUP CHAT, Phase 3b — web's client for `POST /api/group-chat/<action>` (the phones post the same body
 * to the native-data bridge as `group-<action>`), and the small client-side state the group actions share:
 * pending posts, rows that arrived over the realtime channel, the notice a lost group leaves behind, and what a
 * leave / removal / end clears. The decisions are in `lib/group-chat-actions.ts` (pure, tested); this file only
 * moves data.
 *
 * Nothing here ever names the CALLER: the route takes identity from the session. The ids in a body name the
 * conversation, invite or member acted on.
 */
import { useSyncExternalStore } from 'react';
import { API_BASE } from './api-base';
import { apiFetch, apiFetchStream } from './api-client';
import { invalidateChats, patchChatsConversation } from './chats';
import { clearChatsCache } from './chats-cache';
import { chatHistoryCache } from './chat-session';
import { queryClient, queryKeys } from './query-client';
import type { Message } from './use-chat';
import {
  interpretGroupAnswer,
  LOCAL_ENDED_LINE_PREFIX,
  lostAccessNotice,
  normalizeRefusal,
  lostGroupState,
  pendingPostRow,
  postMessageBody,
  refusalKey,
  type GroupActionOutcome,
  type GroupChatAction,
  type LostGroupState,
  type PendingGroupPost,
} from './group-chat-actions';
import { endedSummary, parseGroupEvent, parseGroupMessageMeta, parseMessageAuthor, rowGroupSummary, viewerRole, type GroupSummary } from './group-chat';
import { parseGroupApproval, parseMaestroUnavailable } from './group-maestro';
import { groupFileRefOf } from './group-files';
import {
  notificationErrorKey,
  notificationsFromRow,
  notificationWriteBody,
  optimisticNotifications,
  parseGroupNotifications,
  type GroupNotifications,
  type NotificationChoice,
} from './group-notifications';
import { pinsAfterRow, pinsAfterWindow, starredAfterWindow, withStar, type GroupPin } from './group-message-actions';

export async function groupChatAction<T = Record<string, unknown>>(action: GroupChatAction, body: Record<string, unknown>): Promise<GroupActionOutcome<T>> {
  try {
    const res = await apiFetch(`${API_BASE}/api/group-chat/${action}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => null);
    return interpretGroupAnswer<T>(res.status, json);
  } catch {
    return { kind: 'failed', status: 0 };
  }
}

/**
 * A group call that must outlive the page (2026-11-01: the presence heartbeat's BACKGROUND on `pagehide`): native
 * `fetch` with `keepalive`, which the buffered http client cannot carry. Fire and forget — no answer is read.
 */
export function groupChatBeacon(action: GroupChatAction, body: Record<string, unknown>): void {
  try {
    void apiFetchStream(`${API_BASE}/api/group-chat/${action}`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }).catch(() => undefined);
  } catch {
    // A hint: nothing to do.
  }
}

// ─── A message off the wire ──────────────────────────────────────────────────────────────────────────────────

/** A ConversationMessage JSON (the `post-message` answer, a realtime `created` event) as a transcript row. */
export function groupMessageFromWire(raw: unknown): Message | null {
  if (!raw || typeof raw !== 'object') return null;
  const m = raw as Record<string, unknown>;
  if (typeof m.id !== 'string' || !m.id) return null;
  const author = parseMessageAuthor(m.author);
  const groupEvent = parseGroupEvent(m.metadata);
  const groupMeta = parseGroupMessageMeta(m.metadata);
  const groupApproval = parseGroupApproval(m.metadata);
  const maestroUnavailable = parseMaestroUnavailable(m.metadata);
  const createdDate = typeof m.createdDate === 'string' ? m.createdDate : m.createdDate instanceof Date ? m.createdDate.toISOString() : undefined;
  const attachments = groupWireAttachments(m.attachments);
  return {
    id: m.id,
    ...(typeof m.conversationId === 'string' ? { conversationId: m.conversationId } : {}),
    role: typeof m.role === 'string' ? m.role : 'USER',
    content: typeof m.content === 'string' ? m.content : '',
    ...(createdDate ? { createdDate } : {}),
    ...(author ? { author } : {}),
    ...(groupEvent ? { groupEvent } : {}),
    ...(groupMeta ? { groupMeta } : {}),
    ...(groupApproval ? { groupApproval } : {}),
    ...(maestroUnavailable ? { maestroUnavailable } : {}),
    ...(attachments.length > 0 ? { attachments } : {}),
  };
}

/**
 * A Maestro's reply in a group may carry files (Phase 4). They are URLs (or, Phase 7, a `groupFile` reference), never a `vfsPath` — a member cannot read the
 * owner's file system — so only the URL fields are kept and the chip opens the URL.
 */
export function groupWireAttachments(raw: unknown): NonNullable<Message['attachments']> {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((a): NonNullable<Message['attachments']> => {
    if (!a || typeof a !== 'object') return [];
    const r = a as Record<string, unknown>;
    // Phase 7: a file SHARED INTO the group rides by reference (url empty); the card opens it through `read-file`.
    const groupFile = groupFileRefOf(r.groupFile);
    if (groupFile) {
      return [{
        url: '',
        fileName: typeof r.fileName === 'string' && r.fileName ? r.fileName : 'file',
        mimeType: typeof r.mimeType === 'string' ? r.mimeType : 'application/octet-stream',
        groupFile,
      }];
    }
    if (typeof r.url !== 'string' || !/^https:\/\//.test(r.url)) return [];
    return [{
      url: r.url,
      fileName: typeof r.fileName === 'string' && r.fileName ? r.fileName : r.url.split('/').pop() || 'file',
      mimeType: typeof r.mimeType === 'string' ? r.mimeType : 'application/octet-stream',
      ...(typeof r.thumbnailUrl === 'string' && /^https:\/\//.test(r.thumbnailUrl) ? { thumbnailUrl: r.thumbnailUrl } : {}),
      ...(typeof r.size === 'number' ? { size: r.size } : {}),
    }];
  });
}

// ─── The shared store: pending posts, live rows, notices ─────────────────────────────────────────────────────

interface StoreState {
  /** Conversations the harness told us are live groups (GROUP_CHAT_POST_ONLY) before any summary did. */
  forcedGroups: Record<string, true>;
  pending: Record<string, PendingGroupPost[]>;
  live: Record<string, Message[]>;
  /** Phase 10a: rows replaced in place (a tombstone for a message deleted for everyone), by conversation then id. */
  replaced: Record<string, Record<string, Message>>;
  /** Phase 10b: when each replacement was made (a later window supersedes an older one — `adoptGroupWindow`). */
  replacedAt: Record<string, Record<string, number>>;
  /** Phase 10b: the group's pins (oldest first, at most 3), from the latest window, answers and realtime rows. */
  pins: Record<string, GroupPin[]>;
  /** Phase 10b: the viewer's starred message ids per group (every loaded page's, plus local toggles). */
  starred: Record<string, ReadonlySet<string>>;
  /** Phase 10b: rows the viewer deleted for themselves this session (gone at once; Undo puts them back). */
  hidden: Record<string, Record<string, true>>;
  /**
   * Groups the server refused this viewer for (NOT_A_MEMBER / GROUP_ENDED): the chat shows the state in place of the
   * composer — never a private chat. Cleared when a later read of the group succeeds (they were invited back).
   */
  lost: Record<string, LostGroupState>;
  /**
   * Groups that ENDED (2026-10-31): each stays a group on screen — the summary this client held when it ended (or
   * `true` when it held none), its transcript kept, its composer replaced by "This group has ended". Never cleared
   * for the session; a group does not come back.
   */
  ended: Record<string, GroupSummary | true>;
  /** The notice the chat surface shows once (a lost group; Phase 10a: an action's toast, with its vars; Phase 10b: an Undo). */
  notice: { key: string; at: number; vars?: Record<string, string | number>; extraKey?: string; action?: { labelKey: string; run: () => void } } | null;
}
const EMPTY_STARRED: ReadonlySet<string> = new Set();
let state: StoreState = { forcedGroups: {}, pending: {}, live: {}, replaced: {}, replacedAt: {}, pins: {}, starred: {}, hidden: {}, lost: {}, ended: {}, notice: null };
const listeners = new Set<() => void>();
function setState(next: StoreState) {
  state = next;
  listeners.forEach((l) => l());
}
function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
/** The current state, outside React (tests, event handlers). */
export function groupChatSnapshot(): StoreState {
  return state;
}
export function useGroupChatStore(): StoreState {
  return useSyncExternalStore(subscribe, () => state, () => state);
}

/** The newest row that arrived LIVE for a conversation (re-renders only when it changes — the chat list's rows read this). */
export function useLastLiveGroupRow(conversationId: string | null | undefined): Message | undefined {
  const get = () => {
    const list = conversationId ? state.live[conversationId] : undefined;
    return list && list.length ? list[list.length - 1] : undefined;
  };
  return useSyncExternalStore(subscribe, get, get);
}

/** One conversation's ended mark (re-renders only when it changes — the chat list's rows read this). */
export function useEndedGroup(conversationId: string | null | undefined): GroupSummary | true | undefined {
  const get = () => (conversationId ? state.ended[conversationId] : undefined);
  return useSyncExternalStore(subscribe, get, get);
}

/** The rows to fold into one conversation's transcript: what arrived live, then the pending posts. */
export function groupRowsFor(s: StoreState, conversationId: string | null, viewerId: string | undefined): { live: Message[]; pending: Message[]; replaced: Record<string, Message>; hidden: Record<string, true> } {
  if (!conversationId) return { live: [], pending: [], replaced: {}, hidden: {} };
  return {
    replaced: s.replaced[conversationId] ?? {},
    hidden: s.hidden[conversationId] ?? {},
    live: s.live[conversationId] ?? [],
    pending: (s.pending[conversationId] ?? []).map((p) => pendingPostRow(p, viewerId) as unknown as Message),
  };
}

function upsertPending(post: PendingGroupPost) {
  const list = (state.pending[post.conversationId] ?? []).filter((p) => p.clientMessageId !== post.clientMessageId);
  setState({ ...state, pending: { ...state.pending, [post.conversationId]: [...list, post] } });
}
function removePending(conversationId: string, clientMessageId: string) {
  const list = (state.pending[conversationId] ?? []).filter((p) => p.clientMessageId !== clientMessageId);
  setState({ ...state, pending: { ...state.pending, [conversationId]: list } });
}
/** Phase 10c: a typed post's bubble while its file is on the way (lib/group-content-api.ts drives it). */
let retryTypedUpload: ((conversationId: string, clientMessageId: string) => void) | null = null;
export function registerTypedUploadRetry(fn: (conversationId: string, clientMessageId: string) => void) {
  retryTypedUpload = fn;
}
export function putPendingGroupPost(post: PendingGroupPost) {
  upsertPending(post);
}
export function dropPendingGroupPost(conversationId: string, clientMessageId: string) {
  removePending(conversationId, clientMessageId);
}
export function pendingGroupPost(conversationId: string, clientMessageId: string): PendingGroupPost | undefined {
  return (state.pending[conversationId] ?? []).find((p) => p.clientMessageId === clientMessageId);
}

/** A message that arrived live (the realtime channel, or our own post's answer). Deduped by id. */
export function appendLiveGroupMessage(message: Message) {
  const id = message.conversationId;
  if (!id || !message.id) return;
  const list = state.live[id] ?? [];
  if (list.some((m) => m.id === message.id)) return;
  setState({ ...state, live: { ...state.live, [id]: [...list, message] } });
}

export function showGroupNotice(key: string, vars?: Record<string, string | number>, more?: { action?: { labelKey: string; run: () => void }; extraKey?: string }) {
  setState({ ...state, notice: { key, at: Date.now(), ...(vars ? { vars } : {}), ...(more?.action ? { action: more.action } : {}), ...(more?.extraKey ? { extraKey: more.extraKey } : {}) } });
}

/** Phase 10b: the open chat re-reads its window (a "Delete for me" undone after a reload dropped the row). */
const reloadListeners = new Set<(conversationId: string) => void>();
export function onGroupReloadRequested(listener: (conversationId: string) => void): () => void {
  reloadListeners.add(listener);
  return () => reloadListeners.delete(listener);
}
export function requestGroupReload(conversationId: string) {
  reloadListeners.forEach((l) => l(conversationId));
}

/**
 * Phase 10a: a row changed in place — the tombstone `delete-message` answered, or one the realtime channel sent. It
 * replaces that id wherever the transcript holds it (loaded window, live rows), and is kept for the session.
 */
export function replaceGroupMessage(message: Message) {
  const id = message.conversationId;
  if (!id || !message.id) return;
  const live = (state.live[id] ?? []).map((m) => (m.id === message.id ? message : m));
  // Phase 10b: a row that gained or lost `groupPinned` (or became a tombstone) moves the pin banner with it.
  const pins = pinsAfterRow(state.pins[id] ?? [], message);
  setState({
    ...state,
    live: { ...state.live, [id]: live },
    replaced: { ...state.replaced, [id]: { ...(state.replaced[id] ?? {}), [message.id]: message } },
    replacedAt: { ...state.replacedAt, [id]: { ...(state.replacedAt[id] ?? {}), [message.id]: Date.now() } },
    ...(pins !== state.pins[id] ? { pins: { ...state.pins, [id]: pins } } : {}),
  });
}

// ─── Phase 10b: pins, stars, delete for me ───────────────────────────────────────────────────────────────────

/** How long a local replacement outlives a window read that may have started before it (ms). */
const REPLACEMENT_GRACE_MS = 3000;

/**
 * A window landed. The LATEST page's pins replace the list (an older page never clears the banner); every page's
 * stars join the set; and a replacement made before this read (a reaction, an edit) yields to the server's row, so
 * a missed realtime event cannot pin a stale copy over a fresh window.
 */
export function adoptGroupWindow(conversationId: string, window: { pinnedMessages?: unknown; starredMessageIds?: unknown; messageIds?: readonly string[] }, latest: boolean) {
  // An ENDED group keeps no pins, whatever a window read still lists (`markGroupEnded` cleared them).
  const pins = state.ended[conversationId] ? state.pins[conversationId] : pinsAfterWindow(state.pins[conversationId] ?? [], window.pinnedMessages, latest);
  const starred = starredAfterWindow(state.starred[conversationId] ?? EMPTY_STARRED, window.starredMessageIds);
  let replaced = state.replaced[conversationId];
  let replacedAt = state.replacedAt[conversationId];
  if (latest && replaced && window.messageIds?.length) {
    const cutoff = Date.now() - REPLACEMENT_GRACE_MS;
    const stale = window.messageIds.filter((mid) => replaced?.[mid] && !replaced[mid].groupMeta?.deleted && (replacedAt?.[mid] ?? 0) < cutoff);
    if (stale.length) {
      replaced = { ...replaced };
      replacedAt = { ...(replacedAt ?? {}) };
      for (const mid of stale) {
        delete replaced[mid];
        delete replacedAt[mid];
      }
    }
  }
  if (pins === state.pins[conversationId] && starred === state.starred[conversationId] && replaced === state.replaced[conversationId]) return;
  setState({
    ...state,
    ...(pins ? { pins: { ...state.pins, [conversationId]: pins } } : {}),
    starred: { ...state.starred, [conversationId]: starred },
    ...(replaced ? { replaced: { ...state.replaced, [conversationId]: replaced } } : {}),
    ...(replacedAt ? { replacedAt: { ...state.replacedAt, [conversationId]: replacedAt } } : {}),
  });
}

export function setGroupPins(conversationId: string, pins: GroupPin[]) {
  setState({ ...state, pins: { ...state.pins, [conversationId]: pins } });
}

export function groupStarred(s: StoreState, conversationId: string | null): ReadonlySet<string> {
  return (conversationId && s.starred[conversationId]) || EMPTY_STARRED;
}

export function setGroupStar(conversationId: string, messageId: string, starred: boolean) {
  const next = withStar(state.starred[conversationId] ?? EMPTY_STARRED, messageId, starred);
  if (next === state.starred[conversationId]) return;
  setState({ ...state, starred: { ...state.starred, [conversationId]: next } });
}

export function setGroupHidden(conversationId: string, messageId: string, hidden: boolean) {
  const current = state.hidden[conversationId] ?? {};
  if (!!current[messageId] === hidden) return;
  const { [messageId]: _drop, ...rest } = current;
  setState({ ...state, hidden: { ...state.hidden, [conversationId]: hidden ? { ...current, [messageId]: true } : rest } });
}
export function clearGroupNotice() {
  if (state.notice) setState({ ...state, notice: null });
}

/**
 * Post (or re-post) one message. The bubble shows at once, keyed by its clientMessageId; success swaps it for the
 * server's row, a refusal or a transport failure leaves it failed-with-retry, and a retry reuses the SAME id, so
 * the server never stores it twice. A lost group (NOT_A_MEMBER / GROUP_ENDED) clears the chat.
 */
export async function postGroupMessage(post: Omit<PendingGroupPost, 'state' | 'errorKey'>): Promise<GroupActionOutcome> {
  upsertPending({ ...post, state: 'sending' });
  const outcome = await groupChatAction('post-message', postMessageBody({ ...post, state: 'sending' }));
  if (outcome.kind === 'ok') {
    const message = groupMessageFromWire(outcome.data.message);
    if (message) appendLiveGroupMessage({ ...message, conversationId: message.conversationId ?? post.conversationId });
    removePending(post.conversationId, post.clientMessageId);
    invalidateChats();
    return outcome;
  }
  upsertPending({ ...post, state: 'failed', errorKey: outcome.kind === 'refused' ? refusalKey(outcome.refusal) : refusalKey(null) });
  if (outcome.kind === 'refused') handleLostAccess(post.conversationId, outcome.refusal);
  // Phase 10a: only admins may send now — re-read the members so the composer turns into the admins-only line.
  if (outcome.kind === 'refused' && outcome.refusal === 'SEND_RESTRICTED') {
    void queryClient.invalidateQueries({ queryKey: ['group-members', post.conversationId] });
  }
  return outcome;
}

/**
 * Phase 4: the harness refused a direct turn with GROUP_CHAT_POST_ONLY — this chat is a live group. Remember that (so
 * the summary is loaded through `members` and the composer posts from now on), and re-post the SAME text through
 * `post-message` under a fresh clientMessageId. The refused send's bubble was already dropped, so this one replaces it.
 */
export function resendAsGroupPost(conversationId: string, text: string, now = new Date()): Promise<GroupActionOutcome> | null {
  if (!state.forcedGroups[conversationId]) setState({ ...state, forcedGroups: { ...state.forcedGroups, [conversationId]: true } });
  const trimmed = text.trim();
  if (!trimmed) return null;
  return postGroupMessage({ clientMessageId: crypto.randomUUID(), conversationId, text: trimmed, mentionedMetaSiteIds: [], createdDate: now.toISOString() });
}

/** Retry a failed post — same clientMessageId, same text, same mentions and reply. */
export function retryGroupPost(conversationId: string, clientMessageId: string): void {
  const post = (state.pending[conversationId] ?? []).find((p) => p.clientMessageId === clientMessageId);
  if (!post || post.state !== 'failed') return;
  // Phase 10c: a file's retry re-uploads only when no file id was minted (lib/group-content-api.ts).
  if (post.upload) {
    retryTypedUpload?.(conversationId, clientMessageId);
    return;
  }
  const { state: _s, errorKey: _e, ...rest } = post;
  void postGroupMessage(rest);
}

// ─── Leaving a group, being removed, ending it ───────────────────────────────────────────────────────────────

/** The in-memory transcript cache `use-chat` keeps (registered there, so this file does not import the hook). */
let forgetTranscript: ((conversationId: string) => void) | null = null;
export function registerTranscriptForget(fn: (conversationId: string) => void) {
  forgetTranscript = fn;
}

/** Listeners that leave the open chat (the chat surface registers one). */
const leaveListeners = new Set<(conversationId: string) => void>();
export function onGroupConversationGone(listener: (conversationId: string) => void): () => void {
  leaveListeners.add(listener);
  return () => leaveListeners.delete(listener);
}

/**
 * The viewer is no longer in this group: drop its row from every list, clear every cached copy of its transcript
 * (the in-memory windows, the chats page's disk page, the pending and live rows here), tell the open chat to
 * leave, and show the notice.
 */
export function dropGroupConversation(conversationId: string, noticeKey: string, lost?: LostGroupState) {
  patchChatsConversation(conversationId, null);
  const legacy = queryClient.getQueryData<{ conversations: { id?: string }[]; hasMore: boolean }>(queryKeys.conversations);
  if (legacy) {
    queryClient.setQueryData(queryKeys.conversations, { ...legacy, conversations: legacy.conversations.filter((c) => c.id !== conversationId) });
  }
  queryClient.removeQueries({ queryKey: queryKeys.conversation(conversationId) });
  queryClient.removeQueries({ queryKey: ['group-members', conversationId] });
  for (const key of [...chatHistoryCache.keys()]) {
    try {
      if ((JSON.parse(key) as unknown[])[1] === conversationId) chatHistoryCache.delete(key);
    } catch {
      // not a [owner, id] key
    }
  }
  forgetTranscript?.(conversationId);
  void clearChatsCache();
  const { [conversationId]: _p, ...pending } = state.pending;
  const { [conversationId]: _l, ...live } = state.live;
  const { [conversationId]: _r, ...replaced } = state.replaced;
  const { [conversationId]: _ra, ...replacedAt } = state.replacedAt;
  const { [conversationId]: _pi, ...pins } = state.pins;
  const { [conversationId]: _s, ...starred } = state.starred;
  const { [conversationId]: _h, ...hidden } = state.hidden;
  if (lost) {
    // Refused by the server: the open chat stays and shows the state itself (no toast, no leaving).
    setState({ ...state, pending, live, replaced, replacedAt, pins, starred, hidden, lost: { ...state.lost, [conversationId]: lost } });
    return;
  }
  const { [conversationId]: _lost, ...keptLost } = state.lost;
  setState({ ...state, pending, live, replaced, replacedAt, pins, starred, hidden, lost: keptLost, notice: { key: noticeKey, at: Date.now() } });
  leaveListeners.forEach((l) => l(conversationId));
}

/** A read of the group succeeded again (re-invited): the lost state goes. */
export function clearLostGroup(conversationId: string) {
  if (!state.lost[conversationId]) return;
  const { [conversationId]: _l, ...lost } = state.lost;
  setState({ ...state, lost });
}

/** Mark a group lost from what a live row says (removed / ended), without a refusal. */
export function markLostGroup(conversationId: string, lost: LostGroupState) {
  if (state.lost[conversationId] === lost) return;
  dropGroupConversation(conversationId, '', lost);
}

/** The group summary this client holds for a conversation (a chats row, the members read, the conversation row). */
export function cachedGroupSummary(conversationId: string): GroupSummary | null {
  for (const [, data] of queryClient.getQueriesData<{ pages?: { conversations?: { id?: string; group?: unknown; endedGroup?: unknown }[] }[] }>({ queryKey: queryKeys.chatsAll })) {
    for (const page of data?.pages ?? []) {
      const row = page.conversations?.find((r) => r.id === conversationId);
      const parsed = rowGroupSummary(row);
      if (parsed) return parsed;
    }
  }
  const members = queryClient.getQueryData<{ summary?: GroupSummary | null }>(['group-members', conversationId]);
  if (members?.summary) return members.summary;
  const row = queryClient.getQueryData<{ group?: unknown; endedGroup?: unknown } | null>(queryKeys.conversation(conversationId));
  const parsed = rowGroupSummary(row);
  if (parsed) return parsed;
  const ended = state.ended[conversationId];
  return ended && ended !== true ? ended : null;
}

/** The viewer owns this group (by the summary this client holds): an owner's chat is never "lost" — it stays theirs. */
function viewerOwns(summary: GroupSummary | null): boolean {
  if (!summary) return false;
  return viewerRole(summary) === 'OWNER' || (!!summary.ownerMetaSiteId && summary.ownerMetaSiteId === summary.viewerId);
}

/**
 * The lost state a live `kind` row puts this viewer in: a MEMBER_REMOVED about them. An ENDED group is never "lost"
 * (it stays a group with no composer — `liveRowEndsGroup` / `markGroupEnded`).
 */
export function lostStateFromLiveRow(conversationId: string, row: Message): LostGroupState | null {
  const event = row.groupMeta?.infoEvent;
  if (!event) return null;
  const summary = cachedGroupSummary(conversationId);
  const viewerId = summary?.viewerId;
  if (event.kind === 'MEMBER_REMOVED' && viewerId && event.subjectMetaSiteId === viewerId) return 'removed';
  return null;
}

/** A live row that says the group ended — from anyone, the viewer included (a GROUP_ENDED event, or an ENDED line). */
export function liveRowEndsGroup(row: Pick<Message, 'groupEvent' | 'groupMeta'>): boolean {
  return row.groupMeta?.infoEvent?.kind === 'GROUP_ENDED' || row.groupEvent?.type === 'ENDED';
}

/**
 * The group ENDED (the End action succeeded; a live GROUP_ENDED; a GROUP_ENDED refusal; a `members` read of an inactive
 * group). It stays a GROUP for the owner and the members alike: the summary this client holds is kept and flagged
 * ended — on the chats row (moved to `endedGroup`, the key the server uses), the conversation row, the `members`
 * read and this store — and NOTHING of the transcript is cleared. The chat draws "This group has ended" in place of
 * its composer and the row reads the same. iOS / Android `markGroupEnded` do the same.
 */
export function markGroupEnded(conversationId: string, known?: GroupSummary | null) {
  const held = known ?? cachedGroupSummary(conversationId);
  const summary = held ? endedSummary(held) : null;
  if (summary) {
    patchChatsConversation(conversationId, { group: undefined, endedGroup: summary });
    const legacy = queryClient.getQueryData<{ conversations: { id?: string; group?: unknown }[]; hasMore: boolean }>(queryKeys.conversations);
    if (legacy) {
      queryClient.setQueryData(queryKeys.conversations, {
        ...legacy,
        conversations: legacy.conversations.map((c) => (c.id === conversationId ? { ...c, group: undefined, endedGroup: summary } : c)),
      });
    }
    queryClient.setQueryData(queryKeys.conversation(conversationId), (row: unknown) => (row && typeof row === 'object' ? { ...(row as object), group: undefined, endedGroup: summary } : row));
  }
  queryClient.setQueryData(['group-members', conversationId], (read: unknown) => {
    if (!read || typeof read !== 'object') return read;
    const r = read as { summary?: GroupSummary | null };
    return r.summary ? { ...r, summary: endedSummary(r.summary) } : read;
  });
  const before = state.ended[conversationId];
  const next: GroupSummary | true = summary ?? (before && before !== true ? before : true);
  // A pending post can never go out now, and an ended group has no pins (no banner, no pinned rows).
  const { [conversationId]: _p, ...pending } = state.pending;
  const { [conversationId]: _l, ...lost } = state.lost;
  const { [conversationId]: _pi, ...pins } = state.pins;
  setState({ ...state, pending, lost, pins, ended: { ...state.ended, [conversationId]: next } });
}

/**
 * Conversations this session opened AT a message — a notification's or a push's link (`?message=`). Such a link was
 * sent to the viewer as a member (an "added you" or "ended" push, a reply in the group), so a NOT_A_MEMBER for it
 * reads "You were removed", not "You're not in this group", even when this client holds no summary for the group
 * any more. iOS / Android treat a chat opened from a push or a `/message/<id>` link the same.
 */
const openedAtMessage = new Set<string>();
export function noteOpenedAtMessage(conversationId: string | null | undefined, messageId: string | null | undefined) {
  if (conversationId && messageId) openedAtMessage.add(conversationId);
}

/**
 * NOT_A_MEMBER / GROUP_ENDED on any group call or read. GROUP_ENDED (for the owner and members alike) keeps the chat a
 * group, ended (`markGroupEnded`) — its transcript and row stay. NOT_A_MEMBER clears the chat, and the open chat shows
 * the lost-group state (`lostGroupState`) in place of its composer; the owner's own chat keeps today's drop. True when
 * it did either.
 */
export function handleLostAccess(conversationId: string, refusal: string | null | undefined): boolean {
  const notice = lostAccessNotice(refusal);
  if (!notice) return false;
  if (refusal && normalizeRefusal(refusal) === 'GROUP_ENDED') {
    markGroupEnded(conversationId);
    return true;
  }
  const summary = cachedGroupSummary(conversationId);
  const wasMember = !!summary || openedAtMessage.has(conversationId);
  const lost = viewerOwns(summary) ? null : lostGroupState(refusal, wasMember);
  dropGroupConversation(conversationId, notice, lost ?? undefined);
  return true;
}

/**
 * The OWNER ended the group: it stays a GROUP, ended (`markGroupEnded`) — its name, people and transcript, no composer —
 * never a private chat. A centred "{You} ended the group" line is appended, the same line a server-written ENDED row draws.
 */
export function applyGroupEnded(conversationId: string, youLabel: string) {
  markGroupEnded(conversationId);
  appendLiveGroupMessage({
    id: `${LOCAL_ENDED_LINE_PREFIX}${conversationId}`,
    conversationId,
    role: 'SYSTEM',
    content: '',
    createdDate: new Date().toISOString(),
    groupEvent: { type: 'ENDED', displayName: youLabel },
  });
}

/**
 * A JOINED group's per-member pin / mute / archive — optimistic on the chats-v1 row (its `pinned` mirrors the
 * member's pin), reconciled from the answer, rolled back on failure. The owner's row never comes here.
 */
export async function setGroupViewerState(conversationId: string, change: { pinned?: boolean; muted?: boolean; archived?: boolean }): Promise<boolean> {
  const before = findChatsViewerState(conversationId);
  patchChatsConversation(conversationId, {
    viewerState: { ...(before ?? {}), ...change },
    ...(change.pinned !== undefined ? { pinned: change.pinned } : {}),
  });
  const outcome = await groupChatAction('viewer-state', { conversationId, ...change });
  if (outcome.kind === 'ok') {
    const vs = outcome.data.viewerState as { pinned?: boolean } | undefined;
    if (vs) patchChatsConversation(conversationId, { viewerState: vs, ...(typeof vs.pinned === 'boolean' ? { pinned: vs.pinned } : {}) });
    // The row-menu Mute and the Notifications picker's Mute are one switch.
    if (change.muted !== undefined) {
      const n = parseGroupNotifications((vs as { notifications?: unknown } | undefined)?.notifications) ?? cachedGroupNotifications(conversationId);
      if (n) applyGroupNotifications(conversationId, { ...n, muted: change.muted });
    }
    return true;
  }
  if (outcome.kind === 'refused' && handleLostAccess(conversationId, outcome.refusal)) return false;
  patchChatsConversation(conversationId, { viewerState: before ?? undefined, ...(change.pinned !== undefined ? { pinned: !change.pinned } : {}) });
  showGroupNotice(outcome.kind === 'refused' ? refusalKey(outcome.refusal) : refusalKey(null));
  return false;
}

/**
 * Put the caller's notification setting everywhere a surface reads it: the chats-v1 row (`groupNotifications`, and a
 * joined member's `viewerState.muted` / `.notifications`) and the cached `members` answer.
 */
export function applyGroupNotifications(conversationId: string, n: GroupNotifications | null): void {
  const before = findChatsViewerState(conversationId);
  patchChatsConversation(conversationId, {
    groupNotifications: n ?? undefined,
    ...(before && n ? { viewerState: { ...before, muted: n.muted, notifications: n } } : {}),
  });
  queryClient.setQueryData(['group-members', conversationId], (read: unknown) =>
    read && typeof read === 'object' ? { ...(read as object), notifications: n } : read,
  );
}

/** The caller's setting as this browser last read it: the `members` answer, else the chats-v1 row. */
export function cachedGroupNotifications(conversationId: string): GroupNotifications | null {
  const read = queryClient.getQueryData<{ notifications?: unknown }>(['group-members', conversationId]);
  const fromMembers = parseGroupNotifications(read?.notifications);
  if (fromMembers) return fromMembers;
  for (const [, data] of queryClient.getQueriesData<{ pages: { conversations: { id: string; groupNotifications?: unknown; viewerState?: unknown }[] }[] }>({ queryKey: queryKeys.chatsAll })) {
    for (const page of data?.pages ?? []) {
      const row = page.conversations.find((r) => r.id === conversationId);
      const n = notificationsFromRow(row);
      if (n) return n;
    }
  }
  return null;
}

/**
 * One notification choice (a mode, "Use default", or Mute) through `viewer-state` — the owner's too. Optimistic,
 * reconciled from the answer's `viewerState.notifications`, rolled back on a refusal or failure. Answers the setting
 * now in force and, on failure, the message key to show.
 */
export async function setGroupNotifications(
  conversationId: string,
  current: GroupNotifications,
  choice: NotificationChoice,
): Promise<{ notifications: GroupNotifications; errorKey: string | null }> {
  const optimistic = optimisticNotifications(current, choice);
  applyGroupNotifications(conversationId, optimistic);
  const outcome = await groupChatAction('viewer-state', notificationWriteBody(conversationId, choice));
  if (outcome.kind === 'ok') {
    const vs = outcome.data.viewerState as { notifications?: unknown } | undefined;
    const confirmed = parseGroupNotifications(vs?.notifications) ?? optimistic;
    applyGroupNotifications(conversationId, confirmed);
    return { notifications: confirmed, errorKey: null };
  }
  if (outcome.kind === 'refused' && handleLostAccess(conversationId, outcome.refusal)) {
    return { notifications: current, errorKey: null };
  }
  applyGroupNotifications(conversationId, current);
  return { notifications: current, errorKey: notificationErrorKey(outcome.kind === 'refused' ? outcome.refusal : null) };
}

function findChatsViewerState(conversationId: string): Record<string, unknown> | null {
  for (const [, data] of queryClient.getQueriesData<{ pages: { conversations: { id: string; viewerState?: unknown }[] }[] }>({ queryKey: queryKeys.chatsAll })) {
    for (const page of data?.pages ?? []) {
      const row = page.conversations.find((r) => r.id === conversationId);
      if (row?.viewerState && typeof row.viewerState === 'object') return row.viewerState as Record<string, unknown>;
    }
  }
  return null;
}
